import { useMutation } from "@apollo/client/react";
import { gql } from "graphql-tag";
import { faTriangleExclamation } from "@fortawesome/pro-solid-svg-icons/faTriangleExclamation";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useCallback, useState } from "react";
import { TokenPayload } from "google-auth-library";

import { ClientFacingErrors } from "@portal/app/_common/types/clientErrors";
import { LoadingIndicator } from "@portal/app/_components/ui-elements/LoadingIndicator";
import { detectKnownClientError } from "@portal/app/_common/lib/clientErrorUtils";
import { LoginWithGoogleDocument } from "./generated";
import { decodeJwt } from "@portal/app/_common/lib/token";
import {
  NO_SHARED_LOGINS_ERROR,
  SIGN_IN_METHOD,
  useSignInContext,
} from "@portal/app/_components/flow/steps/SignInStep";
import { useFlowContext } from "@portal/app/_components/flow/FlowContext";
import { useApolloCache } from "@portal/app/_components/apolloUtils";
import { GoogleAuth } from "@portal/app/login/_components/GoogleAuth";
import { SignInType } from "@portal/app/login/SignInFlowController";
import { useSignUpAnalytics } from "@portal/app/_components/hooks/useSignUpAnalytics";
import { LOCAL_STORAGE_KEYS, useLocalStorage } from "@portal/app/_components/hooks/useLocalStorage";

void gql`
  mutation loginWithGoogle(
    $googleToken: String!
    $affiliateSlug: String
    $affiliateExternalId: String
    $affiliateFirstEncounteredAt: String
  ) {
    loginWithGoogle(
      googleToken: $googleToken
      affiliateSlug: $affiliateSlug
      affiliateExternalId: $affiliateExternalId
      affiliateFirstEncounteredAt: $affiliateFirstEncounteredAt
    ) {
      id
      tokens {
        accessToken
        refreshToken
      }
      customer {
        id
        signInFlow {
          ...SignInFlowFragment
        }
      }
    }
  }
`;

export const GoogleAuthForSignIn = ({
  propsToContainer,
  signInType,
}: {
  propsToContainer?: React.ComponentProps<typeof Box>;
  signInType: SignInType;
}) => {
  const { setClientErrorMessage } = useSignInContext();
  const [authenticateViaGoogleToken] = useMutation(LoginWithGoogleDocument);
  const [errors, setErrors] = useState<{
    source: string;
    response: string;
  } | null>();
  const [googleDetails, setGoogleDetails] = useState<TokenPayload | null>(null);
  const [, setMethod, removeMethod] = useLocalStorage<string>(LOCAL_STORAGE_KEYS.MAGIC_LINK_METHOD);
  const { evictRootQuery } = useApolloCache();
  const getSignUpAnalytics = useSignUpAnalytics();
  const { getFlowArgs } = useFlowContext();

  const onCredentialResponse = useCallback(
    (response: google.accounts.id.CredentialResponse) => {
      setGoogleDetails((decodeJwt(response.credential) as TokenPayload | undefined) ?? null);

      try {
        setMethod(SIGN_IN_METHOD.GOOGLE);
        return authenticateViaGoogleToken({
          variables: {
            googleToken: response.credential,
            ...getSignUpAnalytics(),
            ...getFlowArgs(),
          },
        })
          .then(() => {
            /**
             * When you're an admin, there's no customer ID. Without a customer ID,
             * the signInFlow field on customer will not return. This forces a
             * recalculation of the signInFlow steps.
             */
            evictRootQuery("signInFlow");
          })
          .catch((error) => {
            setGoogleDetails(null);
            if (detectKnownClientError(error) === ClientFacingErrors.UserIsSharedGoogleAccount) {
              setClientErrorMessage(NO_SHARED_LOGINS_ERROR);
            } else {
              setClientErrorMessage({
                title: "Something went wrong",
                description: "Please try again",
              });
            }
            removeMethod();
          });
      } catch (err) {
        setErrors({ source: "Dreambound", response: err.message });
      }
    },
    [
      authenticateViaGoogleToken,
      evictRootQuery,
      getFlowArgs,
      getSignUpAnalytics,
      removeMethod,
      setClientErrorMessage,
      setMethod,
    ]
  );

  return googleDetails ? (
    <LoadingIndicator
      fullScreen
      title={`Hey, ${googleDetails.given_name}!`}
      content="Signing you in..."
    />
  ) : errors ? (
    <Alert color="error" icon={<FontAwesomeIcon icon={faTriangleExclamation} />}>
      <AlertTitle>Google sign-in isn&apos;t working</AlertTitle>
      <Typography>Please use another sign-in method. Sorry!</Typography>
    </Alert>
  ) : (
    <GoogleAuth
      onClick={() => setMethod(SIGN_IN_METHOD.GOOGLE)}
      onSuccess={onCredentialResponse}
      propsToContainer={propsToContainer}
      signInType={signInType}
    />
  );
};
